// src/components/TaraSettingsTab.js
// Settings → Tara tab.
// Manages: tara_settings table (inbox, delivery, pickup, express, payment accounts) + sellers.order_form_enabled/order_form_note
// Tax and advance-payment rules are PRODUCT-LEVEL (read from products.vat_percent etc) — not configured here.

import { useState, useEffect } from 'react'
import { supabase } from '../supabase'

const inputStyle = (focused) => ({
  width: '100%', border: '1px solid ' + (focused ? 'var(--primary)' : 'var(--border)'), borderRadius: 'var(--radius-md)',
  padding: '9px 12px', fontSize: '12.5px', outline: 'none', background: 'var(--bg-card)', color: 'var(--text-primary)',
  fontFamily: 'var(--font-ui)', boxSizing: 'border-box', transition: 'border-color 0.15s',
})

function FieldInput({ label, value, onChange, placeholder, type = 'text', optional, hint }) {
  const [focused, setFocused] = useState(false)
  return (
    <div>
      <label style={{ fontSize: '11.5px', fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: '5px' }}>
        {label}{optional && <span style={{ fontWeight: 400, color: '#B0B3B8', marginLeft: '4px' }}>optional</span>}
      </label>
      <input type={type} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
        style={inputStyle(focused)} onFocus={() => setFocused(true)} onBlur={() => setFocused(false)} />
      {hint && <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '4px' }}>{hint}</div>}
    </div>
  )
}

function FieldTextarea({ label, value, onChange, placeholder, optional, rows = 2, hint }) {
  const [focused, setFocused] = useState(false)
  return (
    <div>
      <label style={{ fontSize: '11.5px', fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: '5px' }}>
        {label}{optional && <span style={{ fontWeight: 400, color: '#B0B3B8', marginLeft: '4px' }}>optional</span>}
      </label>
      <textarea value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} rows={rows}
        style={{ ...inputStyle(focused), resize: 'none' }}
        onFocus={() => setFocused(true)} onBlur={() => setFocused(false)} />
      {hint && <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '4px' }}>{hint}</div>}
    </div>
  )
}

function Card({ children, style = {} }) {
  return <div style={{ background: 'var(--bg-card)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--border)', padding: '20px', ...style }}>{children}</div>
}

function SectionLabel({ children, icon }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px' }}>
      {icon && <span style={{ fontSize: '15px' }}>{icon}</span>}
      <div style={{ fontSize: '10.5px', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.07em' }}>{children}</div>
    </div>
  )
}

function InfoBox({ children, color = 'var(--primary)', bg }) {
  return <div style={{ borderRadius: 'var(--radius-md)', padding: '10px 14px', fontSize: '12px', background: bg || color + '10', border: '1px solid ' + color + '30', color, lineHeight: 1.6, marginBottom: '14px' }}>{children}</div>
}

function Toggle({ label, desc, value, onChange, disabled }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '11px 0', borderBottom: '1px solid #F5F5F5', opacity: disabled ? 0.6 : 1 }}>
      <div style={{ flex: 1, minWidth: 0, marginRight: '16px' }}>
        <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-primary)' }}>{label}</div>
        {desc && <div style={{ fontSize: '11.5px', color: 'var(--text-muted)', marginTop: '2px' }}>{desc}</div>}
      </div>
      <button type="button" disabled={disabled} onClick={() => onChange(!value)}
        style={{ width: '44px', height: '24px', borderRadius: 'var(--radius-lg)', background: value ? 'var(--primary)' : '#D1D5DB', border: 'none', cursor: disabled ? 'not-allowed' : 'pointer', position: 'relative', transition: 'background 0.2s', flexShrink: 0 }}>
        <div style={{ position: 'absolute', top: '2px', width: '20px', height: '20px', borderRadius: '50%', background: 'var(--bg-card)', boxShadow: '0 1px 3px rgba(0,0,0,0.2)', transition: 'left 0.2s', left: value ? '22px' : '2px' }} />
      </button>
    </div>
  )
}

function SaveBtn({ onClick, saving, label }) {
  return (
    <button onClick={onClick} disabled={saving}
      style={{ width: '100%', padding: '12px', borderRadius: 'var(--radius-md)', background: saving ? 'var(--border)' : 'var(--primary)', border: 'none', fontSize: '13px', fontWeight: 700, color: '#fff', cursor: saving ? 'not-allowed' : 'pointer', transition: 'background 0.15s' }}>
      {saving ? '...' : label}
    </button>
  )
}

function TaraStarIcon({ size = 16, color = '#2DD4B0' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <path d="M12 2C12 2 12.8 7.5 14.5 9.5C16.2 11.5 22 12 22 12C22 12 16.2 12.5 14.5 14.5C12.8 16.5 12 22 12 22C12 22 11.2 16.5 9.5 14.5C7.8 12.5 2 12 2 12C2 12 7.8 11.5 9.5 9.5C11.2 7.5 12 2 12 2Z" fill={color}/>
      <path d="M19 3C19 3 19.4 5.2 20.2 6C21 6.8 23 7 23 7C23 7 21 7.2 20.2 8C19.4 8.8 19 11 19 11C19 11 18.6 8.8 17.8 8C17 7.2 15 7 15 7C15 7 17 6.8 17.8 6C18.6 5.2 19 3 19 3Z" fill={color} opacity="0.7"/>
      <path d="M5 16C5 16 5.25 17.3 5.75 17.75C6.25 18.25 7.5 18.5 7.5 18.5C7.5 18.5 6.25 18.75 5.75 19.25C5.25 19.75 5 21 5 21C5 21 4.75 19.75 4.25 19.25C3.75 18.75 2.5 18.5 2.5 18.5C2.5 18.5 3.75 18.25 4.25 17.75C4.75 17.3 5 16 5 16Z" fill={color} opacity="0.5"/>
    </svg>
  )
}

const PAYMENT_METHODS_BY_REGION = [
  { key: 'cod',     label: 'Cash on Delivery (COD)', icon: '💵', region: 'universal' },
  { key: 'bkash',   label: 'bKash',                  icon: '🟣', region: 'BD' },
  { key: 'nagad',   label: 'Nagad',                  icon: '🟠', region: 'BD' },
  { key: 'rocket',  label: 'Rocket',                 icon: '🟣', region: 'BD' },
  { key: 'upay',    label: 'Upay',                   icon: '🔵', region: 'BD' },
  { key: 'bank',    label: 'Bank Transfer',          icon: '🏦', region: 'universal' },
  { key: 'payid',   label: 'PayID',                  icon: '🇦🇺', region: 'AU' },
  { key: 'zelle',   label: 'Zelle',                  icon: '💜', region: 'US' },
  { key: 'wise',    label: 'Wise',                   icon: '🌐', region: 'universal' },
  { key: 'partial', label: 'Advance Payment',        icon: '💳', region: 'universal' },
]

export default function TaraSettingsTab({ seller, showToast }) {
  const [loading, setLoading]   = useState(true)
  const [saving, setSaving]     = useState(false)

  const [inboxEnabled, setInboxEnabled] = useState(false)
  const [savingInbox, setSavingInbox]   = useState(false)

  const [facebookUsername, setFacebookUsername] = useState('')
  const [orderFormEnabled, setOrderFormEnabled] = useState(true)
  const [orderFormNote, setOrderFormNote]       = useState('')
  const [savingSlug, setSavingSlug]             = useState(false)

  const [form, setForm] = useState({
    auto_reply_enabled: true,
    language_mode: 'auto',
    bargaining_enabled: true,
    bargaining_floor_pct: 10,
    delivery_dhaka: '1-2 business days',
    delivery_outside: '3-5 business days',
    delivery_express_note: '',
    delivery_charge_dhaka: 60,
    delivery_charge_outside: 120,
    shop_phone: '',
    escalation_message: '',
    payment_methods_enabled: ['cod'],
    bkash_number: '', nagad_number: '', rocket_number: '', upay_number: '',
    bank_account_name: '', bank_account_number: '', bank_name: '', bank_branch: '', bank_routing_number: '', bank_swift_code: '',
    payid: '', zelle_email: '', wise_email: '',
    // ── partial payment ──────────────────────────────────
    partial_payment_enabled: false,
    partial_minimum_pct: 30,
    // ── pickup & express ─────────────────────────────────
    pickup_enabled: false, pickup_address: '', pickup_hours_note: '', pickup_contact_number: '',
    express_enabled: false, express_note: '',
  })
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }))

  useEffect(() => {
    if (seller?.id) load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seller?.id])

  useEffect(() => {
    setInboxEnabled(!!seller?.tara_inbox_enabled)
  }, [seller?.tara_inbox_enabled])

  const load = async () => {
    setLoading(true)
    setFacebookUsername(seller.facebook_username || '')
    setOrderFormEnabled(seller.order_form_enabled !== false)
    setOrderFormNote(seller.order_form_note || '')
    setInboxEnabled(!!seller.tara_inbox_enabled)

    const { data } = await supabase.from('tara_settings').select('*').eq('seller_id', seller.id).single()
    if (data) {
      setForm(f => ({
        ...f, ...data,
        payment_methods_enabled: data.payment_methods_enabled || ['cod'],
        partial_minimum_pct: data.partial_minimum_pct ?? 30,
        partial_payment_enabled: data.partial_payment_enabled ?? false,
      }))
    }
    setLoading(false)
  }

  const handleToggleInbox = async (newValue) => {
    setInboxEnabled(newValue)
    setSavingInbox(true)
    const { error } = await supabase.from('sellers').update({ tara_inbox_enabled: newValue }).eq('id', seller.id)
    setSavingInbox(false)
    if (error) {
      setInboxEnabled(!newValue)
      showToast('Failed to update — please try again', 'error')
      return
    }
    showToast(newValue ? 'Tara inbox enabled' : 'Tara inbox disabled')
  }

  const handleSaveSlug = async () => {
    setSavingSlug(true)
    await supabase.from('sellers').update({
      order_form_enabled: orderFormEnabled,
      order_form_note: orderFormNote || null,
    }).eq('id', seller.id)
    setSavingSlug(false)
    showToast('Order form settings saved')
  }

  const handleSaveTaraSettings = async () => {
    setSaving(true)
    const payload = {
      seller_id: seller.id,
      auto_reply_enabled: form.auto_reply_enabled,
      language_mode: form.language_mode,
      bargaining_enabled: form.bargaining_enabled,
      bargaining_floor_pct: Number(form.bargaining_floor_pct) || 10,
      delivery_dhaka: form.delivery_dhaka || null,
      delivery_outside: form.delivery_outside || null,
      delivery_express_note: form.delivery_express_note || null,
      delivery_charge_dhaka: Number(form.delivery_charge_dhaka) || 0,
      delivery_charge_outside: Number(form.delivery_charge_outside) || 0,
      shop_phone: form.shop_phone || null,
      escalation_message: form.escalation_message || null,
      payment_methods_enabled: form.payment_methods_enabled,
      bkash_number: form.bkash_number || null,
      nagad_number: form.nagad_number || null,
      rocket_number: form.rocket_number || null,
      upay_number: form.upay_number || null,
      bank_account_name: form.bank_account_name || null,
      bank_account_number: form.bank_account_number || null,
      bank_name: form.bank_name || null,
      bank_branch: form.bank_branch || null,
      bank_routing_number: form.bank_routing_number || null,
      bank_swift_code: form.bank_swift_code || null,
      payid: form.payid || null,
      zelle_email: form.zelle_email || null,
      wise_email: form.wise_email || null,
      // ── partial payment ──────────────────────────────────
      partial_payment_enabled: form.partial_payment_enabled,
      partial_minimum_pct: form.partial_payment_enabled ? (Number(form.partial_minimum_pct) || 30) : null,
      // ── pickup & express ─────────────────────────────────
      pickup_enabled: form.pickup_enabled,
      pickup_address: form.pickup_address || null,
      pickup_hours_note: form.pickup_hours_note || null,
      pickup_contact_number: form.pickup_contact_number || null,
      express_enabled: form.express_enabled,
      express_note: form.express_note || null,
    }
    const { error } = await supabase.from('tara_settings').upsert(payload, { onConflict: 'seller_id' })
    setSaving(false)
    if (error) { showToast('Failed to save — ' + error.message, 'error'); return }
    showToast('Tara settings saved')
  }

  const togglePaymentMethod = (key) => {
    setForm(f => {
      const has = f.payment_methods_enabled.includes(key)
      return { ...f, payment_methods_enabled: has ? f.payment_methods_enabled.filter(m => m !== key) : [...f.payment_methods_enabled, key] }
    })
  }

  const orderFormUrl = facebookUsername ? `app.pageconnect.io/order/${facebookUsername}` : null

  if (loading) {
    return (
      <div style={{ padding: '48px', textAlign: 'center' }}>
        <div style={{ width: '24px', height: '24px', borderRadius: '50%', border: '2.5px solid var(--primary)', borderTopColor: 'transparent', animation: 'tara-spin 0.7s linear infinite', margin: '0 auto 12px' }} />
        <div style={{ fontSize: '13px', color: 'var(--text-muted)' }}>Loading Tara settings...</div>
        <style>{`@keyframes tara-spin{to{transform:rotate(360deg)}}`}</style>
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>

      {/* ── Tara identity header ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '16px 20px', borderRadius: 'var(--radius-lg)', background: 'linear-gradient(135deg,#0B1F3A,#1a3a6e)', color: '#fff' }}>
        <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: 'rgba(255,255,255,0.1)', border: '1.5px solid rgba(45,212,176,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <TaraStarIcon size={20} />
        </div>
        <div>
          <div style={{ fontSize: '15px', fontWeight: 700 }}>Tara — Your AI Business Agent</div>
          <div style={{ fontSize: '12px', opacity: 0.7, marginTop: '2px' }}>Configure how Tara handles your customer inbox and order form</div>
        </div>
      </div>

      {/* ── Inbox automation ── */}
      <Card>
        <SectionLabel icon="🤖">Inbox Automation</SectionLabel>
        <Toggle
          label="Enable Tara for Messenger inbox"
          desc="Tara will read incoming Facebook messages and comments, and auto-reply based on your settings below"
          value={inboxEnabled}
          disabled={savingInbox}
          onChange={handleToggleInbox}
        />
        <Toggle label="Auto-reply" desc="Tara replies automatically without waiting for approval" value={form.auto_reply_enabled} onChange={v => set('auto_reply_enabled', v)} />
        <div style={{ marginTop: '14px' }}>
          <label style={{ fontSize: '11.5px', fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: '5px' }}>Language</label>
          <select value={form.language_mode} onChange={e => set('language_mode', e.target.value)} style={inputStyle(false)}>
            <option value="auto">Auto-detect (recommended)</option>
            <option value="en">Always English</option>
            <option value="bn">Always Bengali</option>
          </select>
        </div>
        <div style={{ marginTop: '14px' }}>
          <Toggle label="Allow bargaining" desc="Tara can negotiate price down to your floor %" value={form.bargaining_enabled} onChange={v => set('bargaining_enabled', v)} />
          {form.bargaining_enabled && (
            <div style={{ marginTop: '8px' }}>
              <FieldInput label="Maximum discount Tara can offer (%)" type="number" value={form.bargaining_floor_pct} onChange={v => set('bargaining_floor_pct', v)} placeholder="10"
                hint="Tara will never go below this — e.g. 10 means max 10% off list price" />
            </div>
          )}
        </div>
      </Card>

      {/* ── Escalation ── */}
      <Card>
        <SectionLabel icon="🙋">Escalation to You</SectionLabel>
        <InfoBox color="var(--text-muted)" bg="var(--bg-muted)">When Tara can't handle a question or a customer asks for a human, she shares this phone number and notifies you.</InfoBox>
        <FieldInput label="Phone number Tara shares" value={form.shop_phone} onChange={v => set('shop_phone', v)} placeholder="01712345678" />
        <div style={{ marginTop: '12px' }}>
          <FieldTextarea label="Custom handoff message" value={form.escalation_message} onChange={v => set('escalation_message', v)} optional
            placeholder="You're now chatting directly with the team — we'll be right with you!" rows={2}
            hint="Leave blank to use the default message" />
        </div>
      </Card>

      {/* ── Delivery & pickup ── */}
      <Card>
        <SectionLabel icon="🚚">Delivery & Pickup</SectionLabel>
        <div style={{ fontSize: '11px', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '10px' }}>Normal delivery</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '14px' }} className="tara-grid">
          <FieldInput label="Dhaka delivery time" value={form.delivery_dhaka} onChange={v => set('delivery_dhaka', v)} placeholder="1-2 business days" />
          <FieldInput label="Outside Dhaka delivery time" value={form.delivery_outside} onChange={v => set('delivery_outside', v)} placeholder="3-5 business days" />
          <FieldInput label="Dhaka delivery charge (৳)" type="number" value={form.delivery_charge_dhaka} onChange={v => set('delivery_charge_dhaka', v)} placeholder="60" />
          <FieldInput label="Outside Dhaka delivery charge (৳)" type="number" value={form.delivery_charge_outside} onChange={v => set('delivery_charge_outside', v)} placeholder="120" />
        </div>
        <FieldInput label="Delivery note" value={form.delivery_express_note} onChange={v => set('delivery_express_note', v)} optional
          placeholder="e.g. Express available in Dhaka for urgent orders" />

        <div style={{ borderTop: '1px solid var(--border)', marginTop: '16px', paddingTop: '16px' }}>
          <Toggle label="Express delivery option" desc="Show 'Express Delivery' choice on order form — customer is told you'll confirm the exact charge" value={form.express_enabled} onChange={v => set('express_enabled', v)} />
          {form.express_enabled && (
            <div style={{ marginTop: '10px' }}>
              <FieldInput label="Express delivery note shown to customer" value={form.express_note} onChange={v => set('express_note', v)} optional
                placeholder="e.g. Same-day delivery available in Dhaka — call to confirm charge" />
            </div>
          )}
        </div>

        <div style={{ borderTop: '1px solid var(--border)', marginTop: '16px', paddingTop: '16px' }}>
          <Toggle label="Store / Collection pickup" desc="Let customers choose to collect their order instead of delivery" value={form.pickup_enabled} onChange={v => set('pickup_enabled', v)} />
          {form.pickup_enabled && (
            <div style={{ marginTop: '10px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <FieldTextarea label="Collection point address" value={form.pickup_address} onChange={v => set('pickup_address', v)}
                placeholder="e.g. Shop 4, Level 2, Bashundhara City, Panthapath, Dhaka" rows={2} />
              <FieldInput label="Open hours" value={form.pickup_hours_note} onChange={v => set('pickup_hours_note', v)} optional
                placeholder="e.g. Sat-Thu 11am-8pm, Fri closed" />
              <FieldInput label="Contact number for pickup" value={form.pickup_contact_number} onChange={v => set('pickup_contact_number', v)} optional
                placeholder="01712345678"
                hint="Shown to customer in case the collection point has different staff than your main shop" />
            </div>
          )}
        </div>
      </Card>

      {/* ── Payment methods ── */}
      <Card>
        <SectionLabel icon="💳">Payment Methods on Order Form</SectionLabel>
        <InfoBox color="var(--text-muted)" bg="var(--bg-muted)">
          We don't process payments — customers send money directly to your account and submit a transaction ID + screenshot as proof. Select which methods to show.
        </InfoBox>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '16px' }}>
          {PAYMENT_METHODS_BY_REGION.map(m => {
            const isOn = form.payment_methods_enabled.includes(m.key)
            return (
              <button key={m.key} onClick={() => togglePaymentMethod(m.key)}
                style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '11px 14px', borderRadius: 'var(--radius-md)', border: '1.5px solid ' + (isOn ? 'var(--primary)' : 'var(--border)'), background: isOn ? 'var(--primary-light)' : 'var(--bg-card)', cursor: 'pointer', textAlign: 'left' }}>
                <span style={{ fontSize: '18px' }}>{m.icon}</span>
                <span style={{ flex: 1, fontSize: '13px', fontWeight: 600, color: isOn ? 'var(--primary)' : 'var(--text-primary)' }}>{m.label}</span>
                {m.region !== 'universal' && <span style={{ fontSize: '10px', fontWeight: 700, padding: '2px 7px', borderRadius: 'var(--radius-full)', background: 'var(--bg-muted)', color: 'var(--text-muted)' }}>{m.region}</span>}
                <div style={{ width: '18px', height: '18px', borderRadius: '5px', border: '2px solid ' + (isOn ? 'var(--primary)' : 'var(--border)'), background: isOn ? 'var(--primary)' : 'var(--bg-card)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  {isOn && <span style={{ color: '#fff', fontSize: '10px', fontWeight: 700 }}>✓</span>}
                </div>
              </button>
            )
          })}
        </div>

        {/* ── Account number inputs — only for enabled methods ── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {form.payment_methods_enabled.includes('bkash') && (
            <FieldInput label="bKash number" value={form.bkash_number} onChange={v => set('bkash_number', v)} placeholder="01712345678"
              hint="This number is shown to customers on the order form" />
          )}
          {form.payment_methods_enabled.includes('nagad') && (
            <FieldInput label="Nagad number" value={form.nagad_number} onChange={v => set('nagad_number', v)} placeholder="01712345678"
              hint="This number is shown to customers on the order form" />
          )}
          {form.payment_methods_enabled.includes('rocket') && (
            <FieldInput label="Rocket number" value={form.rocket_number} onChange={v => set('rocket_number', v)} placeholder="01712345678"
              hint="This number is shown to customers on the order form" />
          )}
          {form.payment_methods_enabled.includes('upay') && (
            <FieldInput label="Upay number" value={form.upay_number} onChange={v => set('upay_number', v)} placeholder="01712345678"
              hint="This number is shown to customers on the order form" />
          )}
          {form.payment_methods_enabled.includes('payid') && (
            <FieldInput label="PayID" value={form.payid} onChange={v => set('payid', v)} placeholder="email@example.com or phone"
              hint="This is shown to customers on the order form" />
          )}
          {form.payment_methods_enabled.includes('zelle') && (
            <FieldInput label="Zelle email or phone" value={form.zelle_email} onChange={v => set('zelle_email', v)} placeholder="email@example.com"
              hint="This is shown to customers on the order form" />
          )}
          {form.payment_methods_enabled.includes('wise') && (
            <FieldInput label="Wise email" value={form.wise_email} onChange={v => set('wise_email', v)} placeholder="email@example.com"
              hint="This is shown to customers on the order form" />
          )}

          {form.payment_methods_enabled.includes('bank') && (
            <div style={{ borderRadius: '12px', border: '1px solid var(--border)', padding: '14px', background: 'var(--bg-muted)' }}>
              <div style={{ fontSize: '11px', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '12px' }}>Bank Transfer Details</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <FieldInput label="Account holder name" value={form.bank_account_name} onChange={v => set('bank_account_name', v)} placeholder="e.g. Riya Akter" />
                <FieldInput label="Account number" value={form.bank_account_number} onChange={v => set('bank_account_number', v)} placeholder="1234567890" />
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }} className="tara-grid">
                  <FieldInput label="Bank name" value={form.bank_name} onChange={v => set('bank_name', v)} placeholder="e.g. BRAC Bank" />
                  <FieldInput label="Branch" value={form.bank_branch} onChange={v => set('bank_branch', v)} placeholder="e.g. Gulshan Branch" optional />
                  <FieldInput label="Routing number" value={form.bank_routing_number} onChange={v => set('bank_routing_number', v)} placeholder="e.g. 123456789" optional />
                  <FieldInput label="SWIFT code" value={form.bank_swift_code} onChange={v => set('bank_swift_code', v)} placeholder="e.g. BRAKBDDH" optional hint="For international transfers" />
                </div>
              </div>
            </div>
          )}

          {/* ── Partial / Advance payment settings ── */}
          {form.payment_methods_enabled.includes('partial') && (
            <div style={{ borderRadius: '12px', border: '1px solid #FDE68A', padding: '14px', background: '#FFFBEB' }}>
              <div style={{ fontSize: '11px', fontWeight: 700, color: '#92400E', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '12px' }}>Advance Payment Settings</div>
              <Toggle
                label="Require minimum advance on order form"
                desc="Customers must pay a minimum % before order is confirmed"
                value={form.partial_payment_enabled}
                onChange={v => set('partial_payment_enabled', v)}
              />
              {form.partial_payment_enabled && (
                <div style={{ marginTop: '10px' }}>
                  <FieldInput
                    label="Minimum advance required (%)"
                    type="number"
                    value={form.partial_minimum_pct}
                    onChange={v => set('partial_minimum_pct', v)}
                    placeholder="30"
                    hint="e.g. 30 means customer must pay at least 30% upfront — rest on delivery. Individual products can override this with their own advance settings."
                  />
                </div>
              )}
              {!form.partial_payment_enabled && (
                <div style={{ marginTop: '8px', fontSize: '12px', color: '#92400E', lineHeight: 1.6 }}>
                  Advance payment amounts are set per product in <strong>Inventory</strong>. Toggle this on to also set a minimum % for all orders globally.
                </div>
              )}
            </div>
          )}
        </div>
      </Card>

      {/* Tax note */}
      <Card>
        <SectionLabel icon="🧾">Tax (VAT / GST)</SectionLabel>
        <InfoBox color="var(--text-muted)" bg="var(--bg-muted)">
          Tax is controlled per product in <strong>Inventory</strong>. If a product has VAT/GST enabled there, it automatically applies on the order form. Products without tax enabled show no tax. There's nothing to configure here.
        </InfoBox>
      </Card>

      <SaveBtn onClick={handleSaveTaraSettings} saving={saving} label="Save Tara Settings" />

      {/* ── Order form link ── */}
      <Card>
        <SectionLabel icon="🔗">Quick Order Form Link</SectionLabel>
        <InfoBox color="var(--text-muted)" bg="var(--bg-muted)">
          This is your permanent order form URL, based on your unique Facebook username. Share it in posts, or Tara will send it automatically during chats.
        </InfoBox>

        <div>
          <label style={{ fontSize: '11.5px', fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: '5px' }}>Your order form URL</label>
          {orderFormUrl ? (
            <div style={{ padding: '10px 14px', borderRadius: 'var(--radius-md)', background: 'var(--primary-light)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px' }}>
              <span style={{ fontSize: '12.5px', fontFamily: 'JetBrains Mono, monospace', color: 'var(--primary)', wordBreak: 'break-all' }}>{orderFormUrl}</span>
              <button onClick={() => { navigator.clipboard.writeText(`https://${orderFormUrl}`); showToast('Link copied') }}
                style={{ flexShrink: 0, fontSize: '11.5px', fontWeight: 600, padding: '5px 12px', borderRadius: 'var(--radius-sm)', background: 'var(--primary)', color: '#fff', border: 'none', cursor: 'pointer' }}>
                Copy
              </button>
            </div>
          ) : (
            <div style={{ padding: '10px 14px', borderRadius: 'var(--radius-md)', background: '#FFFBEB', border: '1px solid #FDE68A', fontSize: '12.5px', color: '#92400E' }}>
              No Facebook username set yet. Add it in Settings → Shop tab to activate your order form link.
            </div>
          )}
          <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '6px' }}>
            🔒 This link is fixed to your Facebook username and can't be changed here — it keeps every shared link working forever.
          </div>
        </div>

        <div style={{ marginTop: '16px' }}>
          <Toggle label="Order form is live" desc="Turn off to pause online orders temporarily" value={orderFormEnabled} onChange={setOrderFormEnabled} />
        </div>

        <div style={{ marginTop: '12px' }}>
          <FieldTextarea label="Note shown on order form" value={orderFormNote} onChange={setOrderFormNote} optional rows={2}
            placeholder="e.g. Eid orders may take 1-2 extra days due to high demand" />
        </div>

        <div style={{ marginTop: '14px' }}>
          <SaveBtn onClick={handleSaveSlug} saving={savingSlug} label="Save Order Form Settings" />
        </div>
      </Card>

      <style>{`
        @media(max-width:640px){
          .tara-grid{grid-template-columns:1fr !important}
        }
      `}</style>
    </div>
  )
}